
import BarChart from '@/components/BarChart.vue'
import RoasByChannel from '@/components/simulationDetails/RoasByChannel.vue'
import TotalSaturationCurve from '@/components/budgetSimulator/TotalSaturationCurve.vue'
import MixByBudget from '@/components/budgetSimulator/MixByBudget.vue'
import { budgetSimulation } from '@/mixins/budgetSimulation.js'
import { roasUnitLabel } from '@/utils/roasUnits.js'
import { zoneBounds } from '@/utils/budgetSimulator.js'

export default {
  mixins: [budgetSimulation],
  components: {
    BarChart,
    RoasByChannel,
    TotalSaturationCurve,
    MixByBudget
  },
  props: {
    simulation: {
      type: Object,
      required: true
    },
    points: {
      type: Array,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    report: {
      type: Object,
      required: false,
      default: null
    },
    colors: {
      type: Object,
      required: false,
      default: () => ({})
    },
    customKpiName: {
      type: String,
      required: false,
      default: ''
    },
    // The KPI to read the points for, chosen by the page that holds the KPI selector.
    kpi: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      currentKpi: this.kpi,
      selectedIndex: 0
    }
  },
  computed: {
    rowsWithRoasByChannel () {
      return this.rows.filter(row => row.roasByChannel && Object.keys(row.roasByChannel).length > 0)
    },
    selectedRow () {
      return this.rowsWithRoasByChannel[Math.min(this.selectedIndex, this.rowsWithRoasByChannel.length - 1)] || {}
    },
    roasUnit () {
      return roasUnitLabel(this.outputUnitName, this.spendUnitName) || ''
    },
    roasAxisName () {
      return this.roasUnit ? `ROAS (${this.roasUnit})` : 'ROAS'
    },
    roasByBudgetData () {
      return {
        x: this.rows.map(row => this.formatValue(row.budget)),
        y: this.rows.map(row => Math.round(row.roas * 10000) / 10000)
      }
    },
    // The share of the reference budget each point is: short enough to label every tick, while the
    // amount of the selected one is shown above the slider.
    sliderLabels () {
      return this.rowsWithRoasByChannel.map(row => `${row.pct}%`)
    },
    roasByChannelData () {
      const data = {}
      const roas = this.selectedRow.roasByChannel || {}
      Object.keys(roas).forEach(channel => {
        data[this.channelLabels[channel] || channel] = roas[channel]
      })
      return data
    },
    prettyColors () {
      const colors = {}
      Object.keys(this.colors).forEach(channel => {
        colors[this.channelLabels[channel] || channel] = this.colors[channel]
      })
      return colors
    }
  },
  methods: {
    // The charts' data as XLSX sheets ([{ title, rows }]) for the page's download, for the KPI
    // being shown. Raw values throughout: the charts abbreviate the budgets, the workbook doesn't.
    // ROAS by Channel goes in for every budget, not only the one the slider is on.
    buildXlsxSheets () {
      const t = key => this.$t('components.budgetSimulator.BudgetSimulation.' + key)
      const tx = key => this.$t('components.ModelReportComponent.' + key)
      const ts = key => this.$t('components.budgetSimulator.TotalSaturationCurve.' + key)
      const pctHeader = t('xlsxPctOfReference')
      const budgetHeader = `${ts('budget')} (${this.spendUnitName})`
      const attributedHeader = `${ts('attributedSales')} (${this.outputUnitName})`
      const roasHeader = this.roasAxisName
      const label = channel => this.channelLabels[channel] || channel
      const sheets = []

      const curve = [[pctHeader, budgetHeader, attributedHeader, roasHeader]]
      this.rows.forEach(row => curve.push([row.pct, row.budget, row.marketingSales, row.roas]))
      sheets.push({ title: t('totalSaturationCurve'), rows: curve })

      // The same zones the chart shades: its x axis reaches the furthest budget or historical point.
      const maxX = Math.max.apply(null, [0].concat(this.rows.map(row => row.budget), this.historical.map(point => point.x)))
      const zones = zoneBounds(this.regions, maxX)
      if (zones) {
        const rows = [[t('xlsxZone'), `${t('xlsxFrom')} (${this.spendUnitName})`, `${t('xlsxTo')} (${this.spendUnitName})`]]
        zones.forEach((zone, i) => rows.push([this.$t(`components.SaturationCurves.zone${i + 2}`), zone[0], zone[1]]))
        sheets.push({ title: t('xlsxOptimalZones'), rows: rows })
      }

      if (this.historical.length > 0) {
        // Each period's own figures, then annualized as the chart plots them.
        const scale = this.historical[0].scale
        const rows = [[t('xlsxPeriod'), budgetHeader, attributedHeader, `${budgetHeader} × ${scale}`, `${attributedHeader} × ${scale}`, roasHeader]]
        this.historical.forEach(point => rows.push([point.date, point.spend, point.sales, point.x, point.y, point.x ? point.y / point.x : null]))
        sheets.push({ title: ts('historical'), rows: rows })
      }

      sheets.push({ title: t('roasByBudget'), rows: [[pctHeader, budgetHeader, roasHeader]].concat(this.rows.map(row => [row.pct, row.budget, row.roas])) })

      const channels = []
      this.rows.forEach(row => {
        Object.keys(row.mix || {}).forEach(channel => {
          if (channels.indexOf(channel) === -1) channels.push(channel)
        })
      })
      channels.sort()
      if (channels.length > 0) {
        const rows = [[pctHeader, budgetHeader].concat(channels.map(channel => `${label(channel)} (%)`))]
        this.rows.forEach(row => {
          rows.push([row.pct, row.budget].concat(channels.map(channel => (row.mix && channel in row.mix) ? row.mix[channel] : null)))
        })
        sheets.push({ title: t('mixByBudget'), rows: rows })
      }

      if (this.rowsWithRoasByChannel.length > 0) {
        const roasLabel = key => this.$t('components.simulationDetails.RoasByChannel.' + key) + (this.roasUnit ? ` (${this.roasUnit})` : '')
        const rows = [[pctHeader, budgetHeader, tx('xlsxChannel'), roasLabel('mean'), roasLabel('lower'), roasLabel('upper')]]
        this.rowsWithRoasByChannel.forEach(row => {
          Object.keys(row.roasByChannel).sort().forEach(channel => {
            const r = row.roasByChannel[channel] || {}
            rows.push([row.pct, row.budget, label(channel), r.mean, r.lower, r.upper])
          })
        })
        sheets.push({ title: this.$t('components.simulationDetails.RoasByChannel.title'), rows: rows })
      }
      return sheets
    }
  },
  watch: {
    kpi (kpi) {
      this.currentKpi = kpi
    },
    // Start on the point closest to the reference budget (100%) once points come in.
    'rowsWithRoasByChannel.length': {
      immediate: true,
      handler (length, previous) {
        if (length === 0 || previous) return
        let best = 0
        this.rowsWithRoasByChannel.forEach((row, i) => {
          if (Math.abs((row.pct || 0) - 100) < Math.abs((this.rowsWithRoasByChannel[best].pct || 0) - 100)) best = i
        })
        this.selectedIndex = best
      }
    }
  }
}
